import React, { useState, useEffect } from 'react';
import { useContent } from '../../contexts/ContentContext';
import './FeaturedProgramsAdmin.css';

const SpecialtiesAdmin = ({ contentPath = 'formations.specialties' }) => {
  const { getContent, updateContent } = useContent();
  const [specialties, setSpecialties] = useState([]);
  const [expandedIndex, setExpandedIndex] = useState(null);

  const allCourses = getContent('formations.formationsGrid.formations') || [];

  useEffect(() => {
    const data = getContent(contentPath);
    setSpecialties(Array.isArray(data) ? data : []);
  }, [contentPath, getContent]);

  const refresh = () => {
    const data = getContent(contentPath);
    setSpecialties(Array.isArray(data) ? data : []);
  };

  const handleUpdate = (index, field, value) => {
    updateContent(`${contentPath}.${index}.${field}`, value);
    refresh();
  };

  const handleCourseToggle = (index, courseId) => {
    const specialty = specialties[index];
    if (!specialty) return;

    const current = specialty.courseIds || [];
    const isAdding = !current.includes(courseId);
    const next = isAdding
      ? [...current, courseId]
      : current.filter((id) => id !== courseId);

    handleUpdate(index, 'courseIds', next);

    const courseIndex = allCourses.findIndex((course) => course.id === courseId);
    if (courseIndex >= 0) {
      updateContent(
        `formations.formationsGrid.formations.${courseIndex}.specialtyId`,
        isAdding ? specialty.id : ''
      );
    }
    refresh();
  };

  const handleAdd = () => {
    const next = [
      ...specialties,
      {
        id: `specialty-${Date.now()}`,
        slug: '',
        title: 'Nouvelle spécialité',
        heroTitle: '',
        description: '',
        icon: 'fas fa-graduation-cap',
        courseIds: [],
      },
    ];
    updateContent(contentPath, next);
    refresh();
    setExpandedIndex(specialties.length);
  };

  const handleDelete = (index) => {
    if (!window.confirm('Supprimer cette spécialité ?')) return;
    const next = specialties.filter((_, i) => i !== index);
    updateContent(contentPath, next);
    refresh();
    setExpandedIndex(null);
  };

  const handleMoveSpecialty = (index, direction) => {
    const next = [...specialties];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= next.length) return;

    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    updateContent(contentPath, next);
    refresh();

    if (expandedIndex === index) {
      setExpandedIndex(targetIndex);
    } else if (expandedIndex === targetIndex) {
      setExpandedIndex(index);
    }
  };

  const specialtyCount = specialties.length;

  const coursesBySpecialty = (specialty) =>
    allCourses.filter(
      (course) =>
        course.specialtyId === specialty.id ||
        (specialty.courseIds || []).includes(course.id)
    );

  const isCourseLinked = (specialty, course) =>
    course.specialtyId === specialty.id ||
    (specialty.courseIds || []).includes(course.id);

  return (
    <div className="specialties-admin">
      <div className="admin-section-header">
        <div className="section-info">
          <h2>
            <i className="fas fa-layer-group" />
            Spécialités et catalogues
          </h2>
          <p>
            Gérez les 6 spécialités et associez les formations (courseIds) à chaque catalogue.
            Utilisez ↑ ↓ pour l&apos;ordre des tuiles sur la page Formations.
          </p>
        </div>
        <button type="button" className="btn-add-specialty" onClick={handleAdd}>
          <i className="fas fa-plus" /> Ajouter une spécialité
        </button>
      </div>

      {specialties.length === 0 && (
        <p className="specialty-empty-hint">Aucune spécialité définie. Exécutez la migration ou ajoutez-en une.</p>
      )}

      {specialties.map((specialty, index) => (
        <div key={specialty.id || index} className="specialty-card">
          <div
            className="specialty-card-header"
            onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <i className={specialty.icon || 'fas fa-folder'} />
              <div>
                <strong>{specialty.title || 'Sans titre'}</strong>
                <div className="specialty-meta">
                  {specialty.slug || specialty.id} · {coursesBySpecialty(specialty).length} formation(s)
                </div>
              </div>
            </div>
            <div className="specialty-card-actions" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="btn-move"
                onClick={() => handleMoveSpecialty(index, 'up')}
                disabled={index === 0}
                title="Remonter"
                aria-label="Remonter cette spécialité"
              >
                <i className="fas fa-arrow-up" />
              </button>
              <button
                type="button"
                className="btn-move"
                onClick={() => handleMoveSpecialty(index, 'down')}
                disabled={index === specialtyCount - 1}
                title="Descendre"
                aria-label="Descendre cette spécialité"
              >
                <i className="fas fa-arrow-down" />
              </button>
              <button type="button" className="btn-delete-specialty" onClick={() => handleDelete(index)}>
                Supprimer
              </button>
              <span>{expandedIndex === index ? '▼' : '▶'}</span>
            </div>
          </div>

          {expandedIndex === index && (
            <div className="specialty-card-body">
              <div className="specialty-form-grid">
                <div className="form-group">
                  <label>ID (stable)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={specialty.id || ''}
                    onChange={(e) => handleUpdate(index, 'id', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Slug URL</label>
                  <input
                    type="text"
                    className="form-input"
                    value={specialty.slug || ''}
                    onChange={(e) => handleUpdate(index, 'slug', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Titre</label>
                  <input
                    type="text"
                    className="form-input"
                    value={specialty.title || ''}
                    onChange={(e) => handleUpdate(index, 'title', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Titre hero (page spécialité)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={specialty.heroTitle || ''}
                    onChange={(e) => handleUpdate(index, 'heroTitle', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Icône FontAwesome</label>
                  <input
                    type="text"
                    className="form-input"
                    value={specialty.icon || ''}
                    onChange={(e) => handleUpdate(index, 'icon', e.target.value)}
                    placeholder="fas fa-tooth"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  className="form-input textarea"
                  rows={3}
                  value={specialty.description || ''}
                  onChange={(e) => handleUpdate(index, 'description', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Formations associées (courseIds)</label>
                <div className="specialty-course-picker">
                  {allCourses.map((course) => {
                    const checked = isCourseLinked(specialty, course);
                    const linkedHere = course.specialtyId === specialty.id;
                    return (
                      <label key={course.id}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleCourseToggle(index, course.id)}
                        />
                        <span>
                          <strong>{course.title}</strong>
                          <br />
                          <small style={{ color: 'var(--admin-text-muted)' }}>
                            {course.id}
                            {linkedHere ? ' · specialtyId OK' : course.specialtyId ? ` · ${course.specialtyId}` : ''}
                          </small>
                        </span>
                      </label>
                    );
                  })}
                </div>
                {coursesBySpecialty(specialty).length > 0 && (
                  <p className="specialty-linked-count">
                    {coursesBySpecialty(specialty).length} formation(s) liée(s) (specialtyId + courseIds)
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default SpecialtiesAdmin;
